/**
 * Report comparison diff helpers. Every financial statement returns arrays of
 * line items keyed by accountId (or code) with integer-cent amounts, plus a set
 * of integer-cent subtotal fields. These utilities outer-join a source-period
 * and target-period response so the UI can render Source | Target | Δ$ | Δ%.
 */

export type DiffRow = {
  key: string;
  code: string;
  name: string;
  sourceCents: number;
  targetCents: number;
  deltaCents: number;
  /** target vs source as a percent of |source|. null when source is 0 (undefined). */
  deltaPct: number | null;
};

export type DiffValue = {
  sourceCents: number;
  targetCents: number;
  deltaCents: number;
  deltaPct: number | null;
};

/** Diff a single pair of subtotal amounts (e.g. net income, total assets). */
export function diffValue(sourceCents: number, targetCents: number): DiffValue {
  const deltaCents = targetCents - sourceCents;
  return {
    sourceCents,
    targetCents,
    deltaCents,
    deltaPct: sourceCents === 0 ? null : (deltaCents / Math.abs(sourceCents)) * 100,
  };
}

/**
 * Outer-join two line-item arrays into diff rows. Rows present in only one
 * period appear with the other side = 0. Source order is preserved; target-only
 * rows are appended in target order — so section groupings stay stable.
 *
 * `keyFn` decides identity (accountId, falling back to code for computed /
 * null-accountId lines like the synthetic retained-earnings row or cash-flow
 * subtotals). `amountFn`/`labelFn` extract the cent amount and display label.
 */
export function diffRows<T>(
  source: T[],
  target: T[],
  keyFn: (row: T) => string,
  amountFn: (row: T) => number,
  labelFn: (row: T) => { code: string; name: string },
): DiffRow[] {
  const targetByKey = new Map<string, T>();
  for (const row of target) targetByKey.get(keyFn(row)) ?? targetByKey.set(keyFn(row), row);

  const seen = new Set<string>();
  const out: DiffRow[] = [];

  const push = (key: string, s: T | undefined, t: T | undefined) => {
    if (seen.has(key)) return;
    seen.add(key);
    const src = s ? amountFn(s) : 0;
    const tgt = t ? amountFn(t) : 0;
    const label = labelFn((t ?? s) as T);
    out.push({
      key,
      code: label.code,
      name: label.name,
      sourceCents: src,
      targetCents: tgt,
      deltaCents: tgt - src,
      deltaPct: src === 0 ? null : ((tgt - src) / Math.abs(src)) * 100,
    });
  };

  for (const s of source) {
    const key = keyFn(s);
    push(key, s, targetByKey.get(key));
  }
  for (const t of target) {
    const key = keyFn(t);
    push(key, undefined, t);
  }
  return out;
}

/** "+12.3%" / "−4.0%" / "—" (when undefined). Rounded to one decimal. */
export function formatDeltaPct(pct: number | null): string {
  if (pct === null || !Number.isFinite(pct)) return '—';
  const rounded = Math.round(pct * 10) / 10;
  const sign = rounded > 0 ? '+' : rounded < 0 ? '−' : '';
  return `${sign}${Math.abs(rounded).toFixed(1)}%`;
}
